import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";

/**
 * Shared hook – returns the promoter's referral code if the current user
 * is an active promoter, otherwise null.
 *
 * Also exposes a `copyRefLink` helper that appends ?ref=CODE to a given URL
 * (defaults to the current page) and copies it to the clipboard.
 */
export default function usePromoterStatus() {
  const user = useSelector(selectUser);
  const [referralCode, setReferralCode] = useState(null);

  useEffect(() => {
    const check = async () => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data } = await supabase
        .from("promoters")
        .select("referral_code")
        .eq("user_id", authUser.id)
        .eq("status", "active")
        .maybeSingle();

      if (data) setReferralCode(data.referral_code);
    };
    if (user?.id) check();
  }, [user?.id]);

  /**
   * Copy a URL with ?ref=<code> to the clipboard.
   * @param {string} [baseUrl] – defaults to window.location.href
   * @returns {Promise<boolean>} – true if copied successfully
   */
  const copyRefLink = async (baseUrl) => {
    if (!referralCode) return false;
    const url = new URL(baseUrl || window.location.href);
    url.searchParams.set("ref", referralCode);
    try {
      await navigator.clipboard.writeText(url.toString());
      return true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url.toString();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    }
  };

  return { isPromoter: !!referralCode, referralCode, copyRefLink };
}
